import { Loader2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync, useAsyncFn } from 'react-use';

import Modal from '@/components/core/modal/Modal';
import PopupNotification from '@/domain/core/PopupNotification';
import { IApplication } from '@/types/applications';
import { Button } from '@/ui/Button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/ui/Select';
import { backendApi } from '@/utils/api';

import cn from './style.module.sass';

interface IProps {
	isOpen: boolean;
	onCloseHandler: () => void;
	selectionId?: number;
}

const ApplicationCloneModal = observer(({ isOpen, onCloseHandler, selectionId }: IProps) => {
	const { t } = useTranslation();
	const { query, push } = useRouter();

	// FIXME: Need to use forwardRef for pass HTML Node
	const [container, setContainer] = useState<HTMLDivElement | null>();
	const [applicationId, setProjectId] = useState(query?.parentId ?? '');
	const [currentSelectionId] = useState(() => selectionId ?? query?.selectionId);

	const { loading: applicationsLoading, value: applications = [] } = useAsync(async () => {
		// TODO: Move to store
		const { data } = await backendApi.post<{ applications: IApplication[] }>('/v2/applications',
            {
                exactMatch: [
                    {
                        column: 'themeId',
                        value: [currentSelectionId]
                    }
                ]
            },
            {
                params: {
                    onlyColumns: ['id', 'name'],
                    page: 0,
                    take: 10000,
                },
            });

		return data?.applications;
	}, []);

	const [{ loading: cloneLoading }, handleClone] = useAsyncFn(async () => {
		try {
			await push({
				pathname: '/form/template',
				query: {
					...query,
					selectionId: currentSelectionId,
					parentId: applicationId,
				},
			});

			onCloseHandler();
		} catch (error) {
			PopupNotification.emitter('error', t('application.cloneError'));

			console.error(error);
		}
	}, [applicationId]);

	useEffect(
		() => () => {
			setProjectId('');
		},
		[]
	);

	return (
		<Modal wrapperClassName={cn.wrapperModal} isOpen={isOpen} handleClose={onCloseHandler}>
			<div className={cn.main}>
				<div className={cn.header}>
					<div className={cn.title}>{t('application.pff')}</div>

					<div className={cn.subtitle}>
						{t('application.copy')}
					</div>
				</div>

				<div ref={setContainer} className={cn.body}>
					<div className={cn.select}>
						<Select disabled={applicationsLoading} onValueChange={setProjectId}>
							<SelectTrigger>
								<SelectValue placeholder={t('common.selectList')} />
							</SelectTrigger>

							<SelectContent container={container}>
								<SelectGroup>
									<SelectLabel>{t('projects.l')}</SelectLabel>

									{applications?.map(({ id, name }) => (
										<SelectItem key={id} value={`${id}`}>
											{name}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					<div className={cn.divider} />

					<div className={cn.buttons}>
						<Button onClick={handleClone} disabled={applicationsLoading || !applicationId || cloneLoading}>
							{cloneLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{t('buttons.accept')}
						</Button>

						<Button variant="tetriary" disabled={cloneLoading} onClick={onCloseHandler}>
							{t('common.c')}
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
});

export { ApplicationCloneModal };
